import React from 'react';
import styled from 'styled-components';
import { FaTimes } from 'react-icons/fa';
import { Sancion, Apelacion } from '../models/types';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 25px;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const ModalHeader = styled.h2`
  color: #1a1a2e;
  margin-top: 0;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  color: #0f3460;
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 1.1rem;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 10px;
  margin-bottom: 10px;
`;

const Label = styled.span`
  font-weight: 600;
  color: #555;
`;

const Value = styled.span`
  color: #333;
`;

const Comentarios = styled.div`
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 6px;
  margin-top: 10px;
  white-space: pre-wrap;
`;

interface SancionModalProps {
  sancion: Sancion | null;
  onClose: () => void;
}

const SancionModal: React.FC<SancionModalProps> = ({ sancion, onClose }) => {
  if (!sancion) return null;

  // Función para formatear la fecha
  const formatearFecha = (fechaStr: string) => {
    if (!fechaStr) return 'Fecha no disponible';
    try {
      const fecha = new Date(fechaStr);
      return fecha.toLocaleDateString('es-ES', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return fechaStr;
    }
  };
  
  // Obtener la apelación asociada si existe
  const apelacion = sancion.reclamacionId 
    ? (sancion as any).apelacion as Apelacion | undefined
    : undefined;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <FaTimes />
        </CloseButton>
        
        <ModalHeader>
          <span>Detalles de la Sanción</span>
        </ModalHeader>
        
        <Section>
          <SectionTitle>Información General</SectionTitle>
          <InfoGrid>
            <Label>Nombre:</Label>
            <Value>{sancion.nombre}</Value>
            
            <Label>Código:</Label>
            <Value>{sancion.codigoSancion || 'N/A'}</Value>
            
            <Label>Puntos de penalización:</Label>
            <Value style={{ color: sancion.puntosPenalizacion < 0 ? '#e94560' : '#4CAF50', fontWeight: 'bold' }}>
              {sancion.puntosPenalizacion > 0 ? '+' : ''}{sancion.puntosPenalizacion} pts
            </Value>
            
            <Label>Fecha de aplicación:</Label>
            <Value>{formatearFecha(sancion.fechaAplicacion)}</Value>
            
            <Label>Tipo:</Label>
            <Value>{sancion.automatica ? 'Automática' : 'Manual'}</Value>
          </InfoGrid>
        </Section>
        
        {sancion.descripcion && (
          <Section>
            <SectionTitle>Descripción</SectionTitle>
            <Comentarios>{sancion.descripcion}</Comentarios>
          </Section>
        )}
        
        {/* Mostrar información de la apelación si existe */}
        {apelacion && (
          <Section>
            <SectionTitle>Información de la Apelación</SectionTitle>
            <InfoGrid>
              <Label>Estado:</Label>
              <Value style={{ 
                color: apelacion.estado === 'aceptada' ? '#4CAF50' : 
                      apelacion.estado === 'rechazada' ? '#e94560' : '#FFC107',
                fontWeight: 'bold'
              }}>
                {apelacion.estado.charAt(0).toUpperCase() + apelacion.estado.slice(1)}
              </Value>
              
              <Label>Motivo de la apelación:</Label>
              <Value>{apelacion.motivo}</Value>
              
              {apelacion.comentariosComisarios && (
                <>
                  <Label>Comentarios de los comisarios:</Label>
                  <Value>
                    <Comentarios>{apelacion.comentariosComisarios}</Comentarios>
                  </Value>
                </>
              )}
              
              {apelacion.fechaResolucion && (
                <>
                  <Label>Fecha de resolución:</Label>
                  <Value>{formatearFecha(apelacion.fechaResolucion)}</Value>
                </>
              )}
            </InfoGrid>
            
            {apelacion.evidencias && apelacion.evidencias.length > 0 && (
              <div style={{ marginTop: '15px' }}>
                <Label>Evidencias adjuntas:</Label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '5px' }}>
                  {apelacion.evidencias.map((evidencia, index) => (
                    <a 
                      key={index} 
                      href={evidencia} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{
                        color: '#0f6bff',
                        textDecoration: 'none',
                        border: '1px solid #0f6bff',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px'
                      }}
                    >
                      Ver evidencia {index + 1}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </Section>
        )}
        
        {sancion.reclamacionId && !apelacion && (
          <Section>
            <SectionTitle>Información de la Reclamación</SectionTitle>
            <InfoGrid>
              <Label>ID de Reclamación:</Label>
              <Value>{sancion.reclamacionId}</Value>
            </InfoGrid>
          </Section>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default SancionModal;
